// ============================================
// CONTRATISTAS.JS — Contratistas externos: cuadrillas, su gente y sus documentos (Bloque 1 + 2, 2026-10-14/15)
// ============================================
// Pestaña de Expedientes (permiso operaciones.contratistas). Pensada para el celular primero: tarjetas,
// hojas que suben desde abajo, botones de 44px. Dos niveles: CONTRATISTA (la empresa) e INTEGRANTE (la
// persona que ejecuta y ocupa el hueco del calendario).
// La REGLA DURA la calcula el servidor (services/contratistas.js): una persona con un documento
// obligatorio vencido o faltante NO esta disponible, y aqui SIEMPRE se dice por que, con el nombre del
// documento y su fecha. Ocultarla sin explicar seria mentir en silencio.
// Bloque 2: invitacion por correo (enlace de un solo uso, 7 dias), solicitudes de reseteo de 2FA que autoriza
// Beep, bitacora de lo que hace el externo, y el aviso de peso de los documentos en la BD.
// Los archivos nunca se abren por URL: se piden con token y se muestran desde un blob.
// Varias piezas (Hoja, formularios, lista de documentos) se EXPORTAN para el portal del externo
// (PortalExterno.js), parametrizadas por la ruta base: mismo componente, distinto endpoint.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Plus, X, Search, Users, FileText, ShieldAlert, ShieldCheck, Clock, Upload, Settings2, Pencil, ExternalLink, Trash2, AlertTriangle,
  Mail, Copy, KeyRound, History, Database, Check,
} from 'lucide-react';
import { API_BASE_EXP, PP_EXP, COL_EXP as COL } from './components/helpersExpedientes';
import { fetchConSesion } from './utils/sesion';
import { authHeaders } from './utils/storage';
import { useAnchoViewport } from './hooks/useResponsive';

// ---- fetch con token (authHeaders de utils/storage; fetchConSesion guarda el token renovado y corta en 401) ----
export async function api(path, opts = {}) {
  const esForm = opts.body instanceof FormData;
  const res = await fetchConSesion(`${API_BASE_EXP}${path}`, {
    ...opts,
    headers: { ...(esForm ? {} : { 'Content-Type': 'application/json' }), ...authHeaders(), ...(opts.headers || {}) },
  });
  let data = null;
  try { data = await res.json(); } catch (e) { /* sin json */ }
  if (!res.ok) { const err = new Error((data && data.error) || `Error ${res.status}`); err.status = res.status; err.data = data; throw err; }
  return data;
}

export const ESTADO_C = {
  invitado:   { label: 'Invitado',   color: '#5E666E', bg: '#EEF0F2' },
  activo:     { label: 'Activo',     color: '#1E7A4C', bg: '#E6F4EC' },
  suspendido: { label: 'Suspendido', color: '#9A5B00', bg: '#FFF4D6' },
  baja:       { label: 'Baja',       color: '#B42318', bg: '#FEE2E2' },
  // Bloque 3: sin trabajos en 120 dias (lo pone el sistema). Se reactiva eligiendo 'Activo'.
  dormido:    { label: 'Dormido',    color: '#5E666E', bg: '#EEF0F2' },
};
export const ESTADO_I = {
  activo:   { label: 'Activo',   color: '#1E7A4C', bg: '#E6F4EC' },
  inactivo: { label: 'Inactivo', color: '#9A5B00', bg: '#FFF4D6' },
  baja:     { label: 'Baja',     color: '#B42318', bg: '#FEE2E2' },
};
export const ESTADO_DOC = {
  vigente:    { label: 'Vigente',    color: '#1E7A4C', bg: '#E6F4EC', Icono: ShieldCheck },
  por_vencer: { label: 'Por vencer', color: '#9A5B00', bg: '#FFF4D6', Icono: Clock },
  vencido:    { label: 'Vencido',    color: '#B42318', bg: '#FEE2E2', Icono: ShieldAlert },
};
export const fmtFecha = (ymd) => { if (!ymd) return '—'; const [a, m, d] = String(ymd).slice(0, 10).split('-').map(Number); return new Date(Date.UTC(a, m - 1, d)).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }); };
const fmtFechaHora = (iso) => iso ? new Date(iso).toLocaleString('es-MX', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';
export const iniciales = (n) => String(n || '?').split(/\s+/).slice(0, 2).map(p => p[0]).join('').toUpperCase();

// ---- estilos base (44px tactiles, Poppins) ----
export const input = { width: '100%', padding: '10px 12px', borderRadius: 10, border: `1px solid ${COL.borde}`, fontSize: 16, fontFamily: PP_EXP, minHeight: 44, boxSizing: 'border-box', color: COL.gris, background: '#fff' };
export const lbl = { fontSize: 12.5, fontWeight: 600, color: COL.grisClaro, marginBottom: 4, display: 'block' };
export const btnP = { background: COL.principal, color: '#fff', border: 'none', borderRadius: 10, padding: '10px 16px', fontSize: 14.5, fontWeight: 600, fontFamily: PP_EXP, cursor: 'pointer', minHeight: 44, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8 };
export const btnS = { ...btnP, background: '#fff', color: COL.gris, border: `1px solid ${COL.borde}` };
export const btnX = { background: 'none', border: 'none', cursor: 'pointer', color: COL.grisClaro, minWidth: 44, minHeight: 44, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: 8 };
export const card = { background: '#fff', border: `1px solid ${COL.borde}`, borderRadius: 14, padding: 14 };
export const Chip = ({ def, children }) => <span style={{ fontSize: 11.5, fontWeight: 700, padding: '3px 9px', borderRadius: 99, color: def.color, background: def.bg, whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 4 }}>{children || def.label}</span>;

// Hoja/modal: en celular sube desde abajo a pantalla casi completa; en PC va centrada.
// Alto con --beep-vh (nunca vh pelado) y cuerpo flex:1 + minHeight:0 para que el pie siempre se alcance.
export function Hoja({ titulo, onCerrar, children, pie, ancho = 640, esCelular }) {
  useEffect(() => { const h = (e) => { if (e.key === 'Escape') onCerrar(); }; window.addEventListener('keydown', h); return () => window.removeEventListener('keydown', h); }, [onCerrar]);
  return (
    <div onClick={onCerrar} style={{ position: 'fixed', inset: 0, background: 'rgba(20,30,40,0.45)', zIndex: 1200, display: 'flex', alignItems: esCelular ? 'flex-end' : 'center', justifyContent: 'center', padding: esCelular ? 0 : 16 }}>
      <div onClick={e => e.stopPropagation()} role="dialog" aria-label={titulo}
        style={{ background: '#fff', width: '100%', maxWidth: esCelular ? '100%' : ancho, borderRadius: esCelular ? '18px 18px 0 0' : 16, maxHeight: 'calc(var(--beep-vh) * 92)', display: 'flex', flexDirection: 'column', boxShadow: '0 10px 40px rgba(0,0,0,0.2)', fontFamily: PP_EXP, color: COL.gris }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 10px 10px 18px', borderBottom: `1px solid ${COL.borde}`, flexShrink: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 16.5, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{titulo}</div>
          <button type="button" onClick={onCerrar} style={btnX} aria-label="Cerrar"><X size={20} /></button>
        </div>
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 18, WebkitOverflowScrolling: 'touch' }}>{children}</div>
        {pie && <div style={{ padding: '10px 18px', borderTop: `1px solid ${COL.borde}`, display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap', flexShrink: 0, paddingBottom: esCelular ? 'max(10px, env(safe-area-inset-bottom))' : 10 }}>{pie}</div>}
      </div>
    </div>
  );
}

export const Aviso = ({ tipo = 'error', children }) => (
  <div style={{ padding: '10px 14px', borderRadius: 10, fontSize: 13.5, lineHeight: 1.5, background: tipo === 'error' ? '#FEE2E2' : (tipo === 'ok' ? '#E6F4EC' : '#FFF4D6'), color: tipo === 'error' ? '#991B1B' : (tipo === 'ok' ? '#1E7A4C' : '#9A5B00'), display: 'flex', gap: 8, alignItems: 'flex-start' }}>
    {tipo === 'ok' ? <Check size={16} style={{ flex: 'none', marginTop: 2 }} /> : <AlertTriangle size={16} style={{ flex: 'none', marginTop: 2 }} />}<div style={{ minWidth: 0 }}>{children}</div>
  </div>
);

// Motivos de bloqueo: SIEMPRE visibles, con documento y fecha.
export function Motivos({ motivos }) {
  if (!motivos || !motivos.length) return null;
  return (
    <ul style={{ margin: '6px 0 0', paddingLeft: 18, fontSize: 12.5, color: '#B42318', lineHeight: 1.55 }}>
      {motivos.map((m, i) => <li key={i}>{m.texto}</li>)}
    </ul>
  );
}

// Contador grande: el estado "activo" no puede mentir. Cuantos de su equipo son asignables HOY y cuantos no.
export function ContadorAsignables({ integrantes, estado, compacto }) {
  const activos = integrantes.filter(i => i.estado === 'activo');
  const ok = activos.filter(i => i.disponibilidad && i.disponibilidad.disponible).length;
  const no = activos.length - ok;
  const celda = (n, texto, color, bg) => (
    <div style={{ flex: 1, minWidth: 120, background: bg, borderRadius: 12, padding: '10px 14px', textAlign: 'center' }}>
      <div style={{ fontSize: compacto ? 30 : 36, fontWeight: 800, color, lineHeight: 1 }}>{n}</div>
      <div style={{ fontSize: 12.5, fontWeight: 600, color, marginTop: 4 }}>{texto}</div>
    </div>
  );
  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        {celda(ok, ok === 1 ? 'persona asignable' : 'personas asignables', '#1E7A4C', '#E6F4EC')}
        {celda(no, no === 1 ? 'persona NO asignable' : 'personas NO asignables', no ? '#B42318' : '#5E666E', no ? '#FEE2E2' : '#EEF0F2')}
      </div>
      {estado === 'activo' && ok === 0 && (
        <div style={{ marginTop: 8 }}><Aviso tipo="warn"><strong>Activo, pero hoy no se le puede asignar nada</strong>: {activos.length ? 'ninguna de sus personas tiene los documentos obligatorios vigentes.' : 'todavia no tiene personas dadas de alta.'}</Aviso></div>
      )}
    </div>
  );
}

// ============================================
// Componente principal (pantalla interna)
// ============================================
export default function Contratistas({ esCelular }) {
  const anchoVp = useAnchoViewport();
  const compacto = esCelular || anchoVp < 1100;
  const [lista, setLista] = useState([]);
  const [familias, setFamilias] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');
  const [fichaId, setFichaId] = useState(null);
  const [modal, setModal] = useState(null); // {tipo:'contratista'|'tipos'|'solicitudes', datos}
  const [almacen, setAlmacen] = useState(null);
  const [solicitudes, setSolicitudes] = useState([]);

  const cargar = useCallback(async () => {
    setCargando(true); setError('');
    try {
      const p = new URLSearchParams(); if (q.trim()) p.set('q', q.trim()); if (filtroEstado) p.set('estado', filtroEstado);
      const r = await api(`/api/contratistas?${p}`);
      setLista(r.contratistas || []); setFamilias(r.familias || []);
    } catch (e) { setError(e.message || 'No se pudieron cargar los contratistas.'); }
    finally { setCargando(false); }
  }, [q, filtroEstado]);
  useEffect(() => { const t = setTimeout(cargar, q ? 300 : 0); return () => clearTimeout(t); }, [cargar, q]);
  const cargarExtras = useCallback(async () => {
    try { setAlmacen((await api('/api/contratistas/almacenamiento')).almacenamiento); } catch (e) { /* el aviso es secundario */ }
    try { setSolicitudes((await api('/api/contratistas/solicitudes-2fa')).solicitudes || []); } catch (e) { /* idem */ }
  }, []);
  useEffect(() => { cargarExtras(); }, [cargarExtras]);

  const etiquetaFam = useMemo(() => Object.fromEntries(familias.map(f => [f.id, f.etiqueta])), [familias]);

  return (
    <div style={{ fontFamily: PP_EXP, color: COL.gris, padding: compacto ? '10px 12px 24px' : '14px 18px 32px' }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 220 }}>
          <Users size={20} color={COL.principal} />
          <div>
            <div style={{ fontWeight: 700, fontSize: 17 }}>Contratistas externos</div>
            <div style={{ fontSize: 12.5, color: COL.grisClaro }}>Cuadrillas que no son empleados: su gente y sus documentos.</div>
          </div>
        </div>
        <button type="button" style={{ ...btnS, position: 'relative' }} onClick={() => setModal({ tipo: 'solicitudes' })} title="Solicitudes de reseteo de 2FA">
          <KeyRound size={17} />{!compacto && 'Reseteos 2FA'}
          {solicitudes.length > 0 && <span style={{ position: 'absolute', top: -6, right: -6, background: '#B42318', color: '#fff', borderRadius: 99, fontSize: 11, fontWeight: 700, minWidth: 20, height: 20, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '0 5px' }}>{solicitudes.length}</span>}
        </button>
        <button type="button" style={btnS} onClick={() => setModal({ tipo: 'tipos' })} title="Tipos de documento"><Settings2 size={17} />{!compacto && 'Tipos de documento'}</button>
        <button type="button" style={btnP} onClick={() => setModal({ tipo: 'contratista', datos: null })}><Plus size={18} /> Contratista</button>
      </div>

      {almacen && almacen.nivel !== 'ok' && (
        <div style={{ marginBottom: 12 }}>
          <Aviso tipo={almacen.nivel === 'rojo' ? 'error' : 'warn'}>
            <strong>Los documentos ya ocupan {almacen.documentos_mb} MB</strong> de los {almacen.umbral_mb} MB fijados como aviso ({almacen.porcentaje}%). Siguen dentro de la base de datos; toca decidir si se mueven a un almacen de archivos. El umbral se ajusta en el Vigilante (clave <code>documentos_mb_aviso</code>).
          </Aviso>
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
          <Search size={16} color={COL.grisClaro} style={{ position: 'absolute', left: 12, top: 14 }} />
          <input style={{ ...input, paddingLeft: 36 }} placeholder="Buscar por nombre, RFC o correo" value={q} onChange={e => setQ(e.target.value)} aria-label="Buscar contratista" />
        </div>
        <select style={{ ...input, width: 'auto', minWidth: 150 }} value={filtroEstado} onChange={e => setFiltroEstado(e.target.value)} aria-label="Filtrar por estado">
          <option value="">Todos los estados</option>
          {Object.entries(ESTADO_C).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
      </div>

      {error && <div style={{ marginBottom: 12 }}><Aviso>{error}</Aviso></div>}
      {cargando && !lista.length ? <div style={{ color: COL.grisClaro, padding: 20, textAlign: 'center' }}>Cargando…</div>
        : !lista.length ? (
          <div style={{ ...card, textAlign: 'center', padding: 28, color: COL.grisClaro }}>
            <Users size={34} style={{ opacity: 0.4 }} />
            <div style={{ marginTop: 8, fontSize: 14 }}>Todavia no hay contratistas. Da de alta el primero con el boton de arriba.</div>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 12, gridTemplateColumns: compacto ? '1fr' : 'repeat(auto-fill, minmax(300px, 1fr))' }}>
            {lista.map(c => <TarjetaContratista key={c.id} c={c} etiquetaFam={etiquetaFam} onAbrir={() => setFichaId(c.id)} />)}
          </div>
        )}
      {almacen && (
        <div style={{ fontSize: 11.5, color: COL.grisClaro, marginTop: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
          <Database size={13} /> Documentos cifrados en la base: {almacen.documentos_mb} MB de {almacen.umbral_mb} MB de aviso · base completa {(almacen.bd_bytes / 1048576).toFixed(0)} MB
        </div>
      )}

      {fichaId && <FichaContratista id={fichaId} esCelular={esCelular} compacto={compacto} familias={familias} onCerrar={() => { setFichaId(null); cargar(); }} />}
      {modal && modal.tipo === 'contratista' && (
        <FormContratista datos={modal.datos} familias={familias} esCelular={esCelular} onCerrar={() => setModal(null)}
          onGuardado={(id) => { setModal(null); cargar(); if (id) setFichaId(id); }} />
      )}
      {modal && modal.tipo === 'tipos' && <TiposDocumento esCelular={esCelular} onCerrar={() => { setModal(null); cargar(); }} />}
      {modal && modal.tipo === 'solicitudes' && <Solicitudes2FA esCelular={esCelular} onCerrar={() => { setModal(null); cargarExtras(); }} />}
    </div>
  );
}

function TarjetaContratista({ c, etiquetaFam, onAbrir }) {
  const fams = (c.familias || []);
  return (
    <div role="button" tabIndex={0} onClick={onAbrir} onKeyDown={e => { if (e.key === 'Enter') onAbrir(); }}
      style={{ ...card, cursor: 'pointer', display: 'grid', gap: 8, minWidth: 0 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 15, overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.nombre}</div>
          <div style={{ fontSize: 12.5, color: COL.grisClaro }}>{c.tipo_persona === 'fisica' ? 'Persona fisica' : 'Persona moral'}{c.rfc ? ` · ${c.rfc}` : ''}{c.usuario_id ? ' · con cuenta' : ''}</div>
        </div>
        <Chip def={ESTADO_C[c.estado] || ESTADO_C.invitado} />
      </div>
      {!!fams.length && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {fams.slice(0, 4).map(f => <span key={f} style={{ fontSize: 11.5, padding: '2px 8px', borderRadius: 99, background: '#EAF2FB', color: COL.oscuro }}>{etiquetaFam[f] || f}</span>)}
          {fams.length > 4 && <span style={{ fontSize: 11.5, color: COL.grisClaro }}>+{fams.length - 4}</span>}
        </div>
      )}
      <div style={{ display: 'flex', gap: 12, fontSize: 13, flexWrap: 'wrap' }}>
        <span><strong>{c.integrantes_activos}</strong> {c.integrantes_activos === 1 ? 'persona' : 'personas'}</span>
        <span style={{ color: '#1E7A4C' }}><strong>{c.integrantes_disponibles}</strong> disponibles</span>
        {c.integrantes_bloqueados > 0 && <span style={{ color: '#B42318' }}><strong>{c.integrantes_bloqueados}</strong> no asignables</span>}
      </div>
      {!!(c.bloqueos_propios || []).length && (
        <div style={{ fontSize: 12.5, color: '#B42318', display: 'flex', gap: 6, alignItems: 'flex-start' }}>
          <ShieldAlert size={15} style={{ flex: 'none', marginTop: 2 }} />
          <span>{c.bloqueos_propios.map(m => m.texto).join(' · ')}</span>
        </div>
      )}
      {c.documentos_por_vencer > 0 && !(c.bloqueos_propios || []).length && (
        <div style={{ fontSize: 12.5, color: '#9A5B00', display: 'flex', gap: 6, alignItems: 'center' }}><Clock size={14} /> {c.documentos_por_vencer} documento(s) por vencer</div>
      )}
    </div>
  );
}

// ============================================
// Ficha del contratista: datos, cuenta, documentos, integrantes y bitacora
// ============================================
function FichaContratista({ id, esCelular, compacto, familias, onCerrar }) {
  const [c, setC] = useState(null);
  const [error, setError] = useState('');
  const [sub, setSub] = useState(null); // {tipo:'editar'|'integrante'|'doc'|'bitacora', datos}
  const [integranteAbierto, setIntegranteAbierto] = useState(null);
  const etiquetaFam = useMemo(() => Object.fromEntries(familias.map(f => [f.id, f.etiqueta])), [familias]);

  const cargar = useCallback(async () => {
    try { const r = await api(`/api/contratistas/${id}`); setC(r.contratista); setError(''); }
    catch (e) { setError(e.message); }
  }, [id]);
  useEffect(() => { cargar(); }, [cargar]);

  const cambiarEstado = async (estado) => {
    if ((estado === 'baja' || estado === 'suspendido') && !window.confirm(estado === 'baja' ? '¿Dar de baja? Su cuenta deja de entrar al instante y su gente deja de ser asignable.' : '¿Suspender? Su cuenta no podra entrar mientras este suspendido.')) return;
    try { await api(`/api/contratistas/${id}/estado`, { method: 'PATCH', body: JSON.stringify({ estado }) }); cargar(); }
    catch (e) { setError(e.message); }
  };
  const cambiarEstadoInt = async (iid, estado) => {
    try { await api(`/api/contratistas/${id}/integrantes/${iid}/estado`, { method: 'PATCH', body: JSON.stringify({ estado }) }); cargar(); }
    catch (e) { setError(e.message); }
  };

  const seccion = { fontSize: 12, fontWeight: 700, color: COL.oscuro, letterSpacing: 0.6, margin: '18px 0 8px', display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'space-between' };
  const dato = (k, v) => v ? <div style={{ minWidth: 0 }}><div style={{ fontSize: 11.5, color: COL.grisClaro }}>{k}</div><div style={{ fontSize: 14, overflowWrap: 'anywhere' }}>{v}</div></div> : null;

  return (
    <Hoja titulo={c ? c.nombre : 'Contratista'} onCerrar={onCerrar} esCelular={esCelular} ancho={820}
      pie={c && <>
        <select style={{ ...input, width: 'auto', minWidth: 160 }} value={c.estado} onChange={e => cambiarEstado(e.target.value)} aria-label="Estado del contratista">
          {Object.entries(ESTADO_C).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <button type="button" style={btnS} onClick={() => setSub({ tipo: 'bitacora' })}><History size={16} /> Actividad</button>
        <button type="button" style={btnS} onClick={() => setSub({ tipo: 'editar' })}><Pencil size={16} /> Editar</button>
      </>}>
      {error && <div style={{ marginBottom: 12 }}><Aviso>{error}</Aviso></div>}
      {!c ? <div style={{ color: COL.grisClaro }}>Cargando…</div> : (
        <>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <Chip def={ESTADO_C[c.estado] || ESTADO_C.invitado} />
            <span style={{ fontSize: 13, color: COL.grisClaro }}>{c.tipo_persona === 'fisica' ? 'Persona fisica' : 'Persona moral'} · {c.factura ? 'Factura' : 'No factura'}</span>
          </div>
          <ContadorAsignables integrantes={c.integrantes} estado={c.estado} compacto={compacto} />
          {!!c.bloqueos_propios.length && (
            <div style={{ marginTop: 10 }}><Aviso tipo="warn"><strong>Su gente no es asignable</strong> mientras falte esto del contratista:<Motivos motivos={c.bloqueos_propios} /></Aviso></div>
          )}

          <div style={seccion}>DATOS</div>
          <div style={{ display: 'grid', gap: 10, gridTemplateColumns: compacto ? '1fr' : '1fr 1fr' }}>
            {dato('RFC', c.rfc)}{dato('Regimen SAT', c.regimen_sat)}{dato('Telefono', c.telefono)}{dato('Correo', c.correo)}
            {dato('Domicilio', c.domicilio)}{dato('Banco', c.banco)}{dato('CLABE', c.clabe)}{dato('Titular de la cuenta', c.cuenta_titular)}
            {dato('Notas', c.notas)}
          </div>
          {!!(c.familias || []).length && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
              {c.familias.map(f => <span key={f} style={{ fontSize: 12, padding: '3px 9px', borderRadius: 99, background: '#EAF2FB', color: COL.oscuro }}>{etiquetaFam[f] || f}</span>)}
            </div>
          )}
          <div style={{ fontSize: 11.5, color: COL.grisClaro, marginTop: 10 }}>
            Alta: {c.creado_por_nombre || 'desconocido'} · {new Date(c.creado_en).toLocaleDateString('es-MX')}{c.actualizado_por_nombre ? ` · Ultimo cambio: ${c.actualizado_por_nombre}` : ''}
          </div>

          <div style={seccion}>CUENTA DE ACCESO</div>
          <CuentaExterna c={c} onCambio={cargar} onError={setError} />

          <div style={seccion}><span>DOCUMENTOS DEL CONTRATISTA</span>
            <button type="button" style={{ ...btnS, minHeight: 36, padding: '6px 12px', fontSize: 13 }} onClick={() => setSub({ tipo: 'doc', datos: { propietario_tipo: 'contratista', propietario_id: c.id, nombre: c.nombre } })}><Upload size={15} /> Subir</button>
          </div>
          <ListaDocumentos docs={c.documentos} onCambio={cargar} onError={setError} />

          <div style={seccion}><span>INTEGRANTES ({c.integrantes.length})</span>
            <button type="button" style={{ ...btnS, minHeight: 36, padding: '6px 12px', fontSize: 13 }} onClick={() => setSub({ tipo: 'integrante', datos: null })}><Plus size={15} /> Integrante</button>
          </div>
          {!c.integrantes.length && <div style={{ fontSize: 13.5, color: COL.grisClaro }}>Aun no hay personas dadas de alta. El calendario ocupa a la persona, no a la empresa: sin integrantes no se le puede programar nada.</div>}
          <ListaIntegrantes integrantes={c.integrantes} compacto={compacto} abierto={integranteAbierto} setAbierto={setIntegranteAbierto}
            onEditar={(i) => setSub({ tipo: 'integrante', datos: i })} onSubir={(i) => setSub({ tipo: 'doc', datos: { propietario_tipo: 'integrante', propietario_id: i.id, nombre: i.nombre } })}
            onEstado={cambiarEstadoInt} onCambio={cargar} onError={setError} />
        </>
      )}

      {sub && sub.tipo === 'editar' && <FormContratista datos={c} familias={familias} esCelular={esCelular} onCerrar={() => setSub(null)} onGuardado={() => { setSub(null); cargar(); }} />}
      {sub && sub.tipo === 'integrante' && <FormIntegrante rutaBase={`/api/contratistas/${id}/integrantes`} datos={sub.datos} esCelular={esCelular} onCerrar={() => setSub(null)} onGuardado={(iid) => { setSub(null); cargar(); if (iid) setIntegranteAbierto(iid); }} />}
      {sub && sub.tipo === 'doc' && <SubirDocumento destino={sub.datos} tipos={c ? c.tipos_documento : []} esCelular={esCelular} onCerrar={() => setSub(null)} onGuardado={() => { setSub(null); cargar(); }} />}
      {sub && sub.tipo === 'bitacora' && <Bitacora id={id} esCelular={esCelular} onCerrar={() => setSub(null)} />}
    </Hoja>
  );
}

// Lista de integrantes con su disponibilidad y motivos. Compartida con el portal del externo.
export function ListaIntegrantes({ integrantes, compacto, abierto, setAbierto, onEditar, onSubir, onEstado, onCambio, onError, rutaDocs = '/api/contratistas/documentos' }) {
  const dato = (k, v) => v ? <div style={{ minWidth: 0 }}><div style={{ fontSize: 11.5, color: COL.grisClaro }}>{k}</div><div style={{ fontSize: 14, overflowWrap: 'anywhere' }}>{v}</div></div> : null;
  const seccion = { fontSize: 12, fontWeight: 700, color: COL.oscuro, letterSpacing: 0.6, margin: '14px 0 8px', display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'space-between' };
  return (
    <div style={{ display: 'grid', gap: 10 }}>
      {integrantes.map(i => {
        const esta = abierto === i.id;
        const d = i.disponibilidad || { disponible: false, motivos: [] };
        return (
          <div key={i.id} style={{ ...card, padding: 12, borderColor: d.disponible ? COL.borde : '#F3B4AE' }}>
            <div role="button" tabIndex={0} onClick={() => setAbierto(esta ? null : i.id)} onKeyDown={e => { if (e.key === 'Enter') setAbierto(esta ? null : i.id); }}
              style={{ display: 'flex', gap: 10, alignItems: 'center', cursor: 'pointer', minHeight: 44 }}>
              <div style={{ width: 40, height: 40, borderRadius: '50%', background: d.disponible ? '#E6F4EC' : '#FEE2E2', color: d.disponible ? '#1E7A4C' : '#B42318', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 14, flex: 'none' }}>{iniciales(i.nombre)}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 14.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{i.nombre}</div>
                <div style={{ fontSize: 12.5, color: COL.grisClaro }}>{i.telefono || 'Sin telefono'}{i.estado !== 'activo' ? ` · ${ESTADO_I[i.estado].label}` : ''}</div>
              </div>
              {d.disponible ? <Chip def={ESTADO_DOC.vigente}><ShieldCheck size={13} /> Disponible</Chip> : <Chip def={ESTADO_DOC.vencido}><ShieldAlert size={13} /> No asignable</Chip>}
            </div>
            {!d.disponible && <Motivos motivos={d.motivos} />}
            {esta && (
              <div style={{ marginTop: 12, borderTop: `1px solid ${COL.borde}`, paddingTop: 12 }}>
                <div style={{ display: 'grid', gap: 10, gridTemplateColumns: compacto ? '1fr' : '1fr 1fr' }}>
                  {dato('NSS', i.nss)}{dato('Contacto de emergencia', i.emergencia_nombre ? `${i.emergencia_nombre}${i.emergencia_telefono ? ` · ${i.emergencia_telefono}` : ''}` : null)}
                  {dato('Certificaciones', i.certificaciones)}
                </div>
                <div style={seccion}><span>DOCUMENTOS</span>
                  <button type="button" style={{ ...btnS, minHeight: 36, padding: '6px 12px', fontSize: 13 }} onClick={() => onSubir(i)}><Upload size={15} /> Subir</button>
                </div>
                <ListaDocumentos docs={i.documentos} onCambio={onCambio} onError={onError} rutaDocs={rutaDocs} />
                <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
                  <button type="button" style={{ ...btnS, minHeight: 40 }} onClick={() => onEditar(i)}><Pencil size={15} /> Editar</button>
                  <select style={{ ...input, width: 'auto', minHeight: 40 }} value={i.estado} onChange={e => onEstado(i.id, e.target.value)} aria-label="Estado del integrante">
                    {Object.entries(ESTADO_I).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                  </select>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// Cuenta de acceso del contratista: invitacion nominal (enlace de un solo uso, 7 dias) o cuenta ya creada.
function CuentaExterna({ c, onCambio, onError }) {
  const [invs, setInvs] = useState(null);
  const [correo, setCorreo] = useState(c.correo || '');
  const [enlace, setEnlace] = useState(null);
  const [copiado, setCopiado] = useState(false);
  const [ocupado, setOcupado] = useState(false);
  const cargar = useCallback(async () => { try { setInvs((await api(`/api/contratistas/${c.id}/invitaciones`)).invitaciones || []); } catch (e) { /* secundario */ } }, [c.id]);
  useEffect(() => { cargar(); }, [cargar]);
  const invitar = async () => {
    setOcupado(true);
    try { const r = await api(`/api/contratistas/${c.id}/invitaciones`, { method: 'POST', body: JSON.stringify({ correo }) }); setEnlace(r.enlace); cargar(); onCambio(); }
    catch (e) { onError(e.message); } finally { setOcupado(false); }
  };
  const revocar = async (id) => {
    try { await api(`/api/contratistas/${c.id}/invitaciones/${id}`, { method: 'DELETE' }); setEnlace(null); cargar(); } catch (e) { onError(e.message); }
  };
  const copiar = async () => { try { await navigator.clipboard.writeText(enlace); setCopiado(true); setTimeout(() => setCopiado(false), 2000); } catch (e) { /* el usuario puede seleccionar el texto */ } };
  if (c.usuario_id) {
    return <div style={{ ...card, padding: 12, display: 'flex', gap: 10, alignItems: 'center', background: '#F8FAFC' }}><ShieldCheck size={20} color="#1E7A4C" /><div style={{ fontSize: 13.5 }}><strong>Tiene cuenta.</strong> Entra con su correo y su aplicacion de codigos (2FA). {c.perfil_completo_at ? 'Alta completada.' : 'Todavia no termina su alta (perfil + 2FA).'}</div></div>;
  }
  const vigente = (invs || []).find(i => i.vigente);
  return (
    <div style={{ ...card, padding: 12, background: '#F8FAFC', display: 'grid', gap: 10 }}>
      {enlace && (
        <Aviso tipo="ok">
          <div><strong>Enlace de invitacion creado.</strong> Mandaselo por WhatsApp o correo; sirve UNA sola vez y caduca en 7 dias. No se vuelve a mostrar.</div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8, flexWrap: 'wrap' }}>
            <code style={{ fontSize: 12, background: '#fff', padding: '6px 8px', borderRadius: 8, border: `1px solid ${COL.borde}`, overflowWrap: 'anywhere', flex: 1, minWidth: 160 }}>{enlace}</code>
            <button type="button" style={{ ...btnS, minHeight: 36 }} onClick={copiar}><Copy size={15} /> {copiado ? 'Copiado' : 'Copiar'}</button>
          </div>
        </Aviso>
      )}
      {vigente && !enlace && <div style={{ fontSize: 13 }}><Clock size={14} style={{ verticalAlign: -2 }} /> Invitacion pendiente para <strong>{vigente.correo}</strong>, vence el {fmtFecha(vigente.vence_at)}. <button type="button" onClick={() => revocar(vigente.id)} style={{ ...btnX, minHeight: 32, color: '#B42318', fontSize: 13, fontFamily: PP_EXP }}>Revocar</button></div>}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ flex: 1, minWidth: 200 }}><label style={lbl}>Correo al que se invita</label><input style={input} type="email" value={correo} onChange={e => setCorreo(e.target.value)} placeholder="contacto@cuadrilla.com" /></div>
        <button type="button" style={{ ...btnP, opacity: ocupado ? 0.65 : 1 }} disabled={ocupado || !correo} onClick={invitar}><Mail size={16} /> {vigente ? 'Nueva invitacion' : 'Invitar'}</button>
      </div>
      <div style={{ fontSize: 12, color: COL.grisClaro, lineHeight: 1.5 }}>Nadie se da de alta solo: solo entra quien reciba este enlace. Al entrar tendra que completar su perfil y activar una aplicacion de codigos (2FA) antes de ver nada.</div>
    </div>
  );
}

// Bitacora: todo lo que hace el externo (entro, vio, descargo, subio...).
function Bitacora({ id, esCelular, onCerrar }) {
  const [filas, setFilas] = useState(null);
  useEffect(() => { api(`/api/contratistas/${id}/bitacora`).then(r => setFilas(r.bitacora || [])).catch(() => setFilas([])); }, [id]);
  const texto = (f) => {
    const d = f.detalle || {};
    if (f.accion === 'peticion') return `${d.metodo} ${d.ruta} → ${d.status}`;
    return `${f.accion.replace(/_/g, ' ')}${d.correo ? ` · ${d.correo}` : ''}${d.autorizado_por ? ` · autorizado por usuario #${d.autorizado_por}` : ''}`;
  };
  return (
    <Hoja titulo="Actividad de la cuenta externa" onCerrar={onCerrar} esCelular={esCelular} ancho={720}>
      {filas === null ? <div style={{ color: COL.grisClaro }}>Cargando…</div> : !filas.length ? <div style={{ color: COL.grisClaro, fontSize: 13.5 }}>Todavia no hay actividad registrada.</div> : (
        <div style={{ display: 'grid', gap: 6 }}>
          {filas.map(f => (
            <div key={f.id} style={{ display: 'flex', gap: 10, fontSize: 12.5, padding: '6px 0', borderBottom: `1px solid ${COL.borde}` }}>
              <span style={{ color: COL.grisClaro, whiteSpace: 'nowrap' }}>{fmtFechaHora(f.fecha)}</span>
              <span style={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>{texto(f)}</span>
              <span style={{ color: COL.grisClaro, whiteSpace: 'nowrap' }}>{f.ip || ''}</span>
            </div>
          ))}
        </div>
      )}
    </Hoja>
  );
}

// Solicitudes de reseteo de 2FA: las pide el externo, las autoriza Beep. Queda quien.
function Solicitudes2FA({ esCelular, onCerrar }) {
  const [filas, setFilas] = useState(null); const [error, setError] = useState('');
  const cargar = useCallback(async () => { try { setFilas((await api('/api/contratistas/solicitudes-2fa?estado=todas')).solicitudes || []); } catch (e) { setError(e.message); } }, []);
  useEffect(() => { cargar(); }, [cargar]);
  const resolver = async (id, accion) => {
    if (accion === 'autorizar' && !window.confirm('¿Autorizar el reseteo? Se borra su segundo factor y se cierran sus sesiones: en la proxima entrada lo configura de nuevo. Quedara registrado que lo autorizaste tu.')) return;
    try { await api(`/api/contratistas/solicitudes-2fa/${id}`, { method: 'PATCH', body: JSON.stringify({ accion }) }); cargar(); } catch (e) { setError(e.message); }
  };
  const EST = { pendiente: { label: 'Pendiente', color: '#9A5B00', bg: '#FFF4D6' }, autorizada: { label: 'Autorizada', color: '#1E7A4C', bg: '#E6F4EC' }, rechazada: { label: 'Rechazada', color: '#B42318', bg: '#FEE2E2' } };
  return (
    <Hoja titulo="Reseteos de 2FA solicitados" onCerrar={onCerrar} esCelular={esCelular} ancho={720}>
      {error && <div style={{ marginBottom: 12 }}><Aviso>{error}</Aviso></div>}
      <div style={{ fontSize: 13, color: COL.grisClaro, marginBottom: 12, lineHeight: 1.5 }}>Un contratista que perdio el telefono y sus codigos de recuperacion lo pide desde el login. Nadie resetea solo: aqui se autoriza, y queda registrado quien lo hizo.</div>
      {filas === null ? <div style={{ color: COL.grisClaro }}>Cargando…</div> : !filas.length ? <div style={{ color: COL.grisClaro, fontSize: 13.5 }}>No hay solicitudes.</div> : (
        <div style={{ display: 'grid', gap: 8 }}>
          {filas.map(s => (
            <div key={s.id} style={{ ...card, padding: 12, display: 'grid', gap: 6 }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 0 }}><div style={{ fontWeight: 600, fontSize: 14 }}>{s.usuario_nombre} · {s.contratista_nombre || 'sin contratista'}</div><div style={{ fontSize: 12.5, color: COL.grisClaro }}>{s.correo} · pedido el {fmtFechaHora(s.creado_en)}{s.ip ? ` desde ${s.ip}` : ''}</div></div>
                <Chip def={EST[s.estado]} />
              </div>
              {s.motivo && <div style={{ fontSize: 13 }}>“{s.motivo}”</div>}
              {s.estado === 'pendiente' ? (
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="button" style={{ ...btnP, minHeight: 40 }} onClick={() => resolver(s.id, 'autorizar')}><KeyRound size={15} /> Autorizar reseteo</button>
                  <button type="button" style={{ ...btnS, minHeight: 40 }} onClick={() => resolver(s.id, 'rechazar')}>Rechazar</button>
                </div>
              ) : <div style={{ fontSize: 12.5, color: COL.grisClaro }}>{s.estado === 'autorizada' ? 'Autorizado' : 'Rechazado'} por {s.resuelto_por_nombre || 'desconocido'} el {fmtFechaHora(s.resuelto_en)}</div>}
            </div>
          ))}
        </div>
      )}
    </Hoja>
  );
}

// Lista de documentos con su estado calculado. "Ver" pide el archivo CON TOKEN y lo abre desde un blob.
export function ListaDocumentos({ docs, onCambio, onError, rutaDocs = '/api/contratistas/documentos' }) {
  const [ocupado, setOcupado] = useState(null);
  const ver = async (d) => {
    setOcupado(d.id);
    try {
      const res = await fetchConSesion(`${API_BASE_EXP}${rutaDocs}/${d.id}/archivo`, { headers: authHeaders() });
      if (!res.ok) throw new Error(res.status === 404 ? 'El documento ya no existe.' : 'No se pudo abrir el documento.');
      const url = URL.createObjectURL(await res.blob());
      const w = window.open(url, '_blank', 'noopener');
      if (!w) { const a = document.createElement('a'); a.href = url; a.download = d.nombre_archivo; a.click(); }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { onError(e.message); }
    finally { setOcupado(null); }
  };
  const quitar = async (d) => {
    if (!window.confirm(`¿Quitar "${d.tipo_nombre}" (${d.nombre_archivo})? Se conserva en el historial, pero deja de contar como vigente.`)) return;
    try { await api(`${rutaDocs}/${d.id}`, { method: 'DELETE' }); onCambio(); } catch (e) { onError(e.message); }
  };
  if (!docs || !docs.length) return <div style={{ fontSize: 13, color: COL.grisClaro }}>Sin documentos.</div>;
  return (
    <div style={{ display: 'grid', gap: 6 }}>
      {docs.map(d => {
        const def = ESTADO_DOC[d.estado] || ESTADO_DOC.vigente;
        return (
          <div key={d.id} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '8px 10px', borderRadius: 10, background: '#F8FAFC', border: `1px solid ${COL.borde}`, minWidth: 0 }}>
            <FileText size={18} color={def.color} style={{ flex: 'none' }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13.5, fontWeight: 600, lineHeight: 1.3, overflowWrap: 'anywhere' }}>{d.tipo_nombre}{d.obligatorio_para_asignar ? ' *' : ''}</div>
              <div style={{ fontSize: 12, color: COL.grisClaro }}>
                {d.fecha_vencimiento ? `Vence ${fmtFecha(d.fecha_vencimiento)}` : 'No caduca'}{d.fecha_emision ? ` · emitido ${fmtFecha(d.fecha_emision)}` : ''}{d.subido_por_nombre ? ` · subio ${d.subido_por_nombre}` : ''}
              </div>
            </div>
            <Chip def={def} />
            <button type="button" style={btnX} onClick={() => ver(d)} disabled={ocupado === d.id} aria-label="Ver documento" title="Ver"><ExternalLink size={18} /></button>
            <button type="button" style={{ ...btnX, color: '#B42318' }} onClick={() => quitar(d)} aria-label="Quitar documento" title="Quitar"><Trash2 size={18} /></button>
          </div>
        );
      })}
    </div>
  );
}

// ============================================
// Formularios (compartidos con el portal del externo)
// ============================================
export function Campo({ label, children, span }) { return <div style={{ gridColumn: span ? '1 / -1' : undefined, minWidth: 0 }}><label style={lbl}>{label}</label>{children}</div>; }

// Campos del contratista. `rutaGuardar` y `metodo` permiten reutilizarlo en el portal del externo (PUT /api/externo/perfil).
export function FormContratista({ datos, familias, esCelular, onCerrar, onGuardado, rutaGuardar, metodo, titulo, textoGuardar = 'Guardar', sinHoja = false, sinBancarios = false }) {
  const [f, setF] = useState(() => ({
    tipo_persona: 'moral', nombre: '', rfc: '', regimen_sat: '', domicilio: '', telefono: '', correo: '', factura: true,
    banco: '', clabe: '', cuenta_titular: '', familias: [], notas: '', ...(datos || {}),
  }));
  const [error, setError] = useState(''); const [guardando, setGuardando] = useState(false);
  const set = (k) => (e) => setF(p => ({ ...p, [k]: e && e.target ? (e.target.type === 'checkbox' ? e.target.checked : e.target.value) : e }));
  const toggleFam = (id) => setF(p => ({ ...p, familias: (p.familias || []).includes(id) ? p.familias.filter(x => x !== id) : [...(p.familias || []), id] }));
  const guardar = async (e) => {
    e.preventDefault(); setGuardando(true); setError('');
    try {
      const body = JSON.stringify(f);
      const ruta = rutaGuardar || (datos && datos.id ? `/api/contratistas/${datos.id}` : '/api/contratistas');
      const met = metodo || (datos && datos.id ? 'PUT' : 'POST');
      const r = await api(ruta, { method: met, body });
      onGuardado(r.id || (datos && datos.id), r);
    } catch (e2) { setError(e2.message); } finally { setGuardando(false); }
  };
  const dos = esCelular ? '1fr' : '1fr 1fr';
  const form = (
    <form id="form-contratista" onSubmit={guardar} style={{ display: 'grid', gap: 12, gridTemplateColumns: dos }}>
      {error && <div style={{ gridColumn: '1 / -1' }}><Aviso>{error}</Aviso></div>}
      <Campo label="Tipo"><select style={input} value={f.tipo_persona} onChange={set('tipo_persona')}><option value="moral">Persona moral (empresa)</option><option value="fisica">Persona fisica</option></select></Campo>
      <Campo label="¿Factura?"><select style={input} value={f.factura ? 'si' : 'no'} onChange={e => setF(p => ({ ...p, factura: e.target.value === 'si' }))}><option value="si">Si, factura</option><option value="no">No factura</option></select></Campo>
      <Campo label={f.tipo_persona === 'fisica' ? 'Nombre completo *' : 'Razon social *'} span><input style={input} value={f.nombre || ''} onChange={set('nombre')} required autoFocus /></Campo>
      <Campo label="RFC"><input style={{ ...input, textTransform: 'uppercase' }} value={f.rfc || ''} onChange={set('rfc')} maxLength={13} /></Campo>
      <Campo label="Regimen SAT"><input style={input} value={f.regimen_sat || ''} onChange={set('regimen_sat')} /></Campo>
      <Campo label={`Telefono${rutaGuardar ? ' *' : ''}`}><input style={input} type="tel" value={f.telefono || ''} onChange={set('telefono')} required={!!rutaGuardar} /></Campo>
      <Campo label={`Correo${rutaGuardar ? ' *' : ''}`}><input style={input} type="email" value={f.correo || ''} onChange={set('correo')} required={!!rutaGuardar} /></Campo>
      <Campo label="Domicilio" span><input style={input} value={f.domicilio || ''} onChange={set('domicilio')} /></Campo>
      <Campo label="Rubros que cubre" span>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {familias.map(fa => {
            const on = (f.familias || []).includes(fa.id);
            return <button key={fa.id} type="button" onClick={() => toggleFam(fa.id)} style={{ minHeight: 36, padding: '6px 12px', borderRadius: 99, border: `1px solid ${on ? COL.principal : COL.borde}`, background: on ? '#EAF2FB' : '#fff', color: on ? COL.oscuro : COL.gris, fontFamily: PP_EXP, fontSize: 13, cursor: 'pointer' }}>{fa.etiqueta}</button>;
          })}
        </div>
      </Campo>
      {!sinBancarios && <>
        <div style={{ gridColumn: '1 / -1', fontSize: 12, fontWeight: 700, color: COL.oscuro, letterSpacing: 0.6, marginTop: 4 }}>DATOS BANCARIOS PARA PAGO</div>
        <Campo label="Banco"><input style={input} value={f.banco || ''} onChange={set('banco')} /></Campo>
        <Campo label="CLABE (18 digitos)"><input style={input} inputMode="numeric" value={f.clabe || ''} onChange={set('clabe')} maxLength={18} /></Campo>
        <Campo label="Titular de la cuenta" span><input style={input} value={f.cuenta_titular || ''} onChange={set('cuenta_titular')} /></Campo>
      </>}
      <Campo label="Notas" span><textarea style={{ ...input, minHeight: 72 }} value={f.notas || ''} onChange={set('notas')} /></Campo>
      {sinHoja && <div style={{ gridColumn: '1 / -1' }}><button type="submit" style={{ ...btnP, width: '100%', opacity: guardando ? 0.65 : 1 }} disabled={guardando}>{guardando ? 'Guardando…' : textoGuardar}</button></div>}
    </form>
  );
  if (sinHoja) return form;
  return (
    <Hoja titulo={titulo || (datos && datos.id ? 'Editar contratista' : 'Nuevo contratista')} onCerrar={onCerrar} esCelular={esCelular}
      pie={<><button type="button" style={btnS} onClick={onCerrar}>Cancelar</button><button type="submit" form="form-contratista" style={{ ...btnP, opacity: guardando ? 0.65 : 1 }} disabled={guardando}>{guardando ? 'Guardando…' : textoGuardar}</button></>}>
      {form}
    </Hoja>
  );
}

export function FormIntegrante({ rutaBase, datos, esCelular, onCerrar, onGuardado }) {
  const [f, setF] = useState(() => ({ nombre: '', telefono: '', nss: '', emergencia_nombre: '', emergencia_telefono: '', certificaciones: '', ...(datos || {}) }));
  const [error, setError] = useState(''); const [guardando, setGuardando] = useState(false);
  const set = (k) => (e) => setF(p => ({ ...p, [k]: e.target.value }));
  const guardar = async (e) => {
    e.preventDefault(); setGuardando(true); setError('');
    try {
      const body = JSON.stringify(f);
      const r = datos && datos.id ? await api(`${rutaBase}/${datos.id}`, { method: 'PUT', body }) : await api(rutaBase, { method: 'POST', body });
      onGuardado(r.id || (datos && datos.id));
    } catch (e2) { setError(e2.message); } finally { setGuardando(false); }
  };
  const dos = esCelular ? '1fr' : '1fr 1fr';
  return (
    <Hoja titulo={datos && datos.id ? 'Editar integrante' : 'Nuevo integrante'} onCerrar={onCerrar} esCelular={esCelular}
      pie={<><button type="button" style={btnS} onClick={onCerrar}>Cancelar</button><button type="submit" form="form-integrante" style={{ ...btnP, opacity: guardando ? 0.65 : 1 }} disabled={guardando}>{guardando ? 'Guardando…' : 'Guardar'}</button></>}>
      <form id="form-integrante" onSubmit={guardar} style={{ display: 'grid', gap: 12, gridTemplateColumns: dos }}>
        {error && <div style={{ gridColumn: '1 / -1' }}><Aviso>{error}</Aviso></div>}
        <Campo label="Nombre completo *" span><input style={input} value={f.nombre || ''} onChange={set('nombre')} required autoFocus /></Campo>
        <Campo label="Telefono"><input style={input} type="tel" value={f.telefono || ''} onChange={set('telefono')} /></Campo>
        <Campo label="NSS (11 digitos)"><input style={input} inputMode="numeric" value={f.nss || ''} onChange={set('nss')} maxLength={11} /></Campo>
        <Campo label="Contacto de emergencia: nombre"><input style={input} value={f.emergencia_nombre || ''} onChange={set('emergencia_nombre')} /></Campo>
        <Campo label="Contacto de emergencia: telefono"><input style={input} type="tel" value={f.emergencia_telefono || ''} onChange={set('emergencia_telefono')} /></Campo>
        <Campo label="Certificaciones" span><textarea style={{ ...input, minHeight: 72 }} value={f.certificaciones || ''} onChange={set('certificaciones')} placeholder="DC-3 alturas 2026, certificacion electrica…" /></Campo>
        <div style={{ gridColumn: '1 / -1', fontSize: 12.5, color: COL.grisClaro }}>El NSS tecleado no prueba nada: lo que cuenta para asignar es la <strong>constancia de vigencia IMSS</strong>, que se sube como documento junto con la identificacion oficial y la fotografia.</div>
      </form>
    </Hoja>
  );
}

export function SubirDocumento({ destino, tipos, esCelular, onCerrar, onGuardado, rutaDocs = '/api/contratistas/documentos' }) {
  const aplicables = tipos.filter(t => t.activo && (t.aplica_a === 'ambos' || t.aplica_a === destino.propietario_tipo));
  const [tipoId, setTipoId] = useState(aplicables[0] ? String(aplicables[0].id) : '');
  const [archivo, setArchivo] = useState(null);
  const [emision, setEmision] = useState(''); const [venc, setVenc] = useState('');
  const [error, setError] = useState(''); const [guardando, setGuardando] = useState(false);
  const tipo = aplicables.find(t => String(t.id) === tipoId);
  const guardar = async (e) => {
    e.preventDefault(); setError('');
    if (!archivo) { setError('Elige un archivo (PDF, JPG o PNG, maximo 10 MB).'); return; }
    if (tipo && tipo.caduca && tipo.obligatorio_para_asignar && !venc) { setError('Este documento es obligatorio para asignar: indica su fecha de vencimiento.'); return; }
    setGuardando(true);
    try {
      const fd = new FormData();
      fd.append('archivo', archivo); fd.append('propietario_tipo', destino.propietario_tipo); fd.append('propietario_id', destino.propietario_id); fd.append('tipo_id', tipoId);
      if (emision) fd.append('fecha_emision', emision); if (venc && tipo && tipo.caduca) fd.append('fecha_vencimiento', venc);
      await api(rutaDocs, { method: 'POST', body: fd });
      onGuardado();
    } catch (e2) { setError(e2.message); } finally { setGuardando(false); }
  };
  return (
    <Hoja titulo={`Subir documento · ${destino.nombre}`} onCerrar={onCerrar} esCelular={esCelular} ancho={520}
      pie={<><button type="button" style={btnS} onClick={onCerrar}>Cancelar</button><button type="submit" form="form-doc" style={{ ...btnP, opacity: guardando ? 0.65 : 1 }} disabled={guardando}><Upload size={16} /> {guardando ? 'Subiendo…' : 'Subir'}</button></>}>
      <form id="form-doc" onSubmit={guardar} style={{ display: 'grid', gap: 12 }}>
        {error && <Aviso>{error}</Aviso>}
        <Campo label="Tipo de documento">
          <select style={input} value={tipoId} onChange={e => setTipoId(e.target.value)}>
            {aplicables.map(t => <option key={t.id} value={t.id}>{t.nombre}{t.obligatorio_para_asignar ? ' (obligatorio para asignar)' : ''}</option>)}
          </select>
        </Campo>
        <Campo label="Archivo (PDF, JPG o PNG, hasta 10 MB)">
          <input style={{ ...input, padding: 8 }} type="file" accept="application/pdf,image/jpeg,image/png" onChange={e => setArchivo(e.target.files && e.target.files[0])} />
        </Campo>
        <div style={{ display: 'grid', gap: 12, gridTemplateColumns: esCelular ? '1fr' : '1fr 1fr' }}>
          <Campo label="Fecha de emision"><input style={input} type="date" value={emision} onChange={e => setEmision(e.target.value)} /></Campo>
          {tipo && tipo.caduca && <Campo label={`Fecha de vencimiento${tipo.obligatorio_para_asignar ? ' *' : ''}`}><input style={input} type="date" value={venc} onChange={e => setVenc(e.target.value)} /></Campo>}
        </div>
        <div style={{ fontSize: 12.5, color: COL.grisClaro, lineHeight: 1.5 }}>Se guarda cifrado dentro de Beep-OS. Solo lo abre quien tiene permiso y cada consulta queda registrada.</div>
      </form>
    </Hoja>
  );
}

// Catalogo de tipos, configurable desde la app (bandera "obligatorio para asignar").
function TiposDocumento({ esCelular, onCerrar }) {
  const [tipos, setTipos] = useState([]); const [error, setError] = useState('');
  const [nuevo, setNuevo] = useState({ nombre: '', aplica_a: 'ambos', obligatorio_para_asignar: false, caduca: true });
  const cargar = useCallback(async () => { try { setTipos((await api('/api/contratistas/tipos-documento')).tipos || []); } catch (e) { setError(e.message); } }, []);
  useEffect(() => { cargar(); }, [cargar]);
  const guardar = async (t, cambios) => {
    try { const r = await api(`/api/contratistas/tipos-documento/${t.id}`, { method: 'PUT', body: JSON.stringify({ ...t, ...cambios }) }); setTipos(r.tipos); }
    catch (e) { setError(e.message); }
  };
  const crear = async (e) => {
    e.preventDefault();
    try { const r = await api('/api/contratistas/tipos-documento', { method: 'POST', body: JSON.stringify(nuevo) }); setTipos(r.tipos); setNuevo({ nombre: '', aplica_a: 'ambos', obligatorio_para_asignar: false, caduca: true }); }
    catch (e2) { setError(e2.message); }
  };
  const sel = { ...input, minHeight: 38, padding: '6px 8px', fontSize: 13.5 };
  const Check2 = ({ v, onChange, label }) => (
    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, minHeight: 36, cursor: 'pointer' }}>
      <input type="checkbox" checked={!!v} onChange={e => onChange(e.target.checked)} style={{ width: 18, height: 18 }} /> {label}
    </label>
  );
  return (
    <Hoja titulo="Tipos de documento" onCerrar={onCerrar} esCelular={esCelular} ancho={720}>
      {error && <div style={{ marginBottom: 12 }}><Aviso>{error}</Aviso></div>}
      <div style={{ fontSize: 13, color: COL.grisClaro, marginBottom: 12, lineHeight: 1.5 }}>
        <strong>Obligatorio para asignar</strong>: si falta o esta vencido, la persona no aparece disponible en el calendario, y la pantalla dice por que.
      </div>
      <div style={{ display: 'grid', gap: 8 }}>
        {tipos.map(t => (
          <div key={t.id} style={{ ...card, padding: 12, opacity: t.activo ? 1 : 0.55 }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <input style={{ ...sel, flex: 1, minWidth: 180 }} value={t.nombre} onChange={e => setTipos(ts => ts.map(x => x.id === t.id ? { ...x, nombre: e.target.value } : x))} onBlur={e => { if (e.target.value.trim() && e.target.value !== t.nombre) guardar(t, { nombre: e.target.value }); }} aria-label="Nombre del tipo" />
              <select style={{ ...sel, width: 'auto' }} value={t.aplica_a} onChange={e => guardar(t, { aplica_a: e.target.value })} aria-label="A quien aplica">
                <option value="ambos">Contratista e integrante</option><option value="contratista">Solo contratista</option><option value="integrante">Solo integrante</option>
              </select>
            </div>
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 4 }}>
              <Check2 v={t.obligatorio_para_asignar} onChange={v => guardar(t, { obligatorio_para_asignar: v })} label="Obligatorio para asignar" />
              <Check2 v={t.caduca} onChange={v => guardar(t, { caduca: v })} label="Caduca" />
              <Check2 v={t.activo} onChange={v => guardar(t, { activo: v })} label="En uso" />
            </div>
          </div>
        ))}
      </div>
      <form onSubmit={crear} style={{ ...card, marginTop: 14, display: 'grid', gap: 10, background: '#F8FAFC' }}>
        <div style={{ fontWeight: 700, fontSize: 13.5 }}>Nuevo tipo</div>
        <input style={input} placeholder="Nombre del documento" value={nuevo.nombre} onChange={e => setNuevo(n => ({ ...n, nombre: e.target.value }))} required />
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center' }}>
          <select style={{ ...sel, width: 'auto' }} value={nuevo.aplica_a} onChange={e => setNuevo(n => ({ ...n, aplica_a: e.target.value }))}>
            <option value="ambos">Contratista e integrante</option><option value="contratista">Solo contratista</option><option value="integrante">Solo integrante</option>
          </select>
          <Check2 v={nuevo.obligatorio_para_asignar} onChange={v => setNuevo(n => ({ ...n, obligatorio_para_asignar: v }))} label="Obligatorio para asignar" />
          <Check2 v={nuevo.caduca} onChange={v => setNuevo(n => ({ ...n, caduca: v }))} label="Caduca" />
          <button type="submit" style={{ ...btnP, marginLeft: 'auto' }}><Plus size={16} /> Agregar</button>
        </div>
      </form>
    </Hoja>
  );
}
